import { useMemo } from "react";
import { filterStations, isPlayable, nearest, playableFirst, type StationV2 } from "@orvis/core";
import { stationKey } from "../state/favorites.js";
import type { BrowseFilterState } from "./useBrowseFilter.js";

/** A station paired with its stable key (computed against the *unfiltered* list). */
export interface KeyedStation {
  station: StationV2;
  key: string;
}

/**
 * The list a TV / Radio tab actually shows: keyed first (so favorites keys
 * don't shift when filtering — `stationKey` falls back to the original
 * index), then filtered, then playable-first or nearest-first.
 */
export function useStationList(
  open: readonly StationV2[] | undefined,
  filter: BrowseFilterState,
  coords?: { lat: number; lng: number }
): KeyedStation[] {
  return useMemo(() => {
    const keyed = new Map<StationV2, string>();
    (open ?? []).forEach((s, i) => keyed.set(s, stationKey(s, i)));
    const filtered = filterStations(open ?? [], filter);
    const ordered = filter.near && coords ? nearest(filtered, coords) : playableFirst(filtered);
    return ordered.map((station) => ({ station, key: keyed.get(station)! }));
  }, [open, filter, coords]);
}

/** Next/previous playable station in `list` relative to `current`, wrapping around. */
export function zapTarget(list: readonly KeyedStation[], current: StationV2 | undefined, dir: 1 | -1): KeyedStation | undefined {
  const playable = list.filter((k) => isPlayable(k.station));
  if (playable.length === 0) return undefined;
  const idx = current ? playable.findIndex((k) => sameStation(k.station, current)) : -1;
  if (idx < 0) return dir === 1 ? playable[0] : playable[playable.length - 1];
  return playable[(idx + dir + playable.length) % playable.length];
}

/** Same station across refetches (query data is replaced, so references change). */
export function sameStation(a: StationV2 | undefined, b: StationV2 | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.id !== undefined ? a.id === b.id : b.id === undefined && a.name === b.name;
}
